import React, { useState, useEffect } from 'react';
import CRUDList from '../Common/CRUDList';
import DateInput from '../Common/DateInput';
import TimeInput from '../Common/TimeInput';
import { MobileTooltip } from '../Common/MobileTooltip';
import {
  flightsStat,
  legs,
  manualFlights,
  legsSchedule,
  routes as routesApi,
  carriers as carriersApi,
  planes as planesApi,
  airports as airportsApi,
  planeRegs as planeRegsApi,
  planeModels as planeModelsApi,
  timezones as timezonesApi,
  passengers as passengersApi
} from '../../services/api';
import { formatDate, formatTime } from '../../utils/dateFormat';
import { flightStatuses, cabinTypes, seatTypes, flightReasons, legTypes } from '../../constants/travelEnums';
import { aircraftManufacturers } from '../../constants/aircraftManufacturers';

const toManufacturerName = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const key = (typeof value === 'string' && /^\d+$/.test(value)) ? parseInt(value, 10) : value;
  return aircraftManufacturers[key] || (typeof value === 'string' ? value : '');
};

const formatDuration = (min) => {
  if (min === null || min === undefined || Number.isNaN(min)) return '-';
  const total = Math.round(min);
  const h = Math.floor(total / 60);
  const m = Math.abs(total % 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const formatDurationHM = (min) => {
  if (min === null || min === undefined || Number.isNaN(min)) return '-';
  const total = Math.round(min);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
};

const durationToTimeInput = (min) => {
  if (min === null || min === undefined || Number.isNaN(min)) return '';
  const total = Math.max(0, Math.round(min));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const timeInputToMinutes = (value) => {
  if (!value) return null;
  const [h, m] = value.split(':').map((v) => parseInt(v, 10));
  if ([h, m].some(Number.isNaN) || m >= 60 || h < 0) return null;
  return h * 60 + m;
};

const formatDurationDiff = (diffMin) => {
  if (diffMin === null || diffMin === undefined || diffMin === 0) return null;
  const total = Math.round(diffMin);
  const sign = total > 0 ? '+' : total < 0 ? '-' : '';
  const h = Math.floor(Math.abs(total) / 60);
  const m = Math.abs(total) % 60;
  if (h === 0) return `${sign}${m}m`;
  if (m === 0) return `${sign}${h}h`;
  return `${sign}${h}:${String(m).padStart(2, '0')}`;
};

const formatOffsetMinutes = (offset) => {
  if (offset === null || offset === undefined || Number.isNaN(offset)) return '';
  const sign = offset >= 0 ? '+' : '-';
  const abs = Math.abs(offset);
  const h = String(Math.floor(abs / 60)).padStart(2, '0');
  const m = String(abs % 60).padStart(2, '0');
  return `UTC${sign}${h}:${m}`;
};

const formatDurationDiffDesc = (diffMin) => {
  if (diffMin === null || diffMin === undefined || diffMin === 0) return null;
  const total = Math.round(diffMin);
  const isSlower = total > 0;
  const h = Math.floor(Math.abs(total) / 60);
  const m = Math.abs(total) % 60;
  
  let timeStr;
  if (h === 0) {
    timeStr = `${m}m`;
  } else if (m === 0) {
    timeStr = `${h}h`;
  } else {
    timeStr = `${h}h ${m}m`;
  }
  
  return isSlower ? `slower by ${timeStr}` : `faster by ${timeStr}`;
};

const formatDistance = (meters) => {
  if (meters === null || meters === undefined) return '-';
  const km = Math.round(meters / 1000);
  return `${km} km`;
};

const formatDelay = (delayMinutes) => {
  if (delayMinutes === null || delayMinutes === undefined || delayMinutes === 0) {
    return { text: 'On time', color: '#27ae60' };
  }
  if (delayMinutes > 0) {
    return { text: `delayed ${delayMinutes}m`, color: '#c0392b' };
  }
  return { text: `early ${Math.abs(delayMinutes)}m`, color: '#27ae60' };
};

const FlightStatusIcon = ({ isCancelled, isCompleted }) => {
  if (isCancelled) {
    return (
      <MobileTooltip title="Cancelled">
        <svg
          role="img"
          aria-label="Cancelled"
          viewBox="0 0 24 24"
          width="20"
          height="20"
          style={{ display: 'block' }}
        >
          <circle cx="12" cy="12" r="9" fill="#fff1f0" stroke="#e74c3c" strokeWidth="2" />
          <path d="M8.5 8.5L15.5 15.5M15.5 8.5L8.5 15.5" fill="none" stroke="#e74c3c" strokeWidth="2.2" strokeLinecap="round" />
        </svg>
      </MobileTooltip>
    );
  }

  return isCompleted ? (
    <MobileTooltip title="Completed">
      <span style={{ fontSize: '1.2em', fontWeight: 'bold', color: '#27ae60' }}>&#10003;</span>
    </MobileTooltip>
  ) : (
    <MobileTooltip title="Scheduled">
      <span style={{ fontSize: '1.2em', fontWeight: 'bold', color: '#f39c12' }}>&#9201;</span>
    </MobileTooltip>
  );
};

function FlightStatForm({ formData, setFormData, passengersList = [], preferredPassengerId }) {
  const [manualFlightsList, setManualFlightsList] = useState([]);
  const [scheduleList, setScheduleList] = useState([]);
  const [legsList, setLegsList] = useState([]);
  const [availableLegsList, setAvailableLegsList] = useState([]);
  const [routesList, setRoutesList] = useState([]);
  const [carriersList, setCarriersList] = useState([]);
  const [planesList, setPlanesList] = useState([]);
  const [airportsList, setAirportsList] = useState([]);
  const [planeRegsList, setPlaneRegsList] = useState([]);
  const [planeModelsList, setPlaneModelsList] = useState([]);
  const [timezonesList, setTimezonesList] = useState([]);
  const [routeError, setRouteError] = useState('');
  const [hydratedManualId, setHydratedManualId] = useState(null);

  const formatCarrierLabel = (carrier) => {
    const iata = carrier.carrier_iata || '-';
    const icao = carrier.carrier_icao || '-';
    return `${carrier.carrier_name} (${iata}/${icao})`;
  };

  const extractIataFromFlightNum = (flightNum) => {
    if (!flightNum) return null;
    const trimmed = String(flightNum).trim().toUpperCase();
    const match = trimmed.match(/^([A-Z0-9]{2})/);
    if (!match) return null;

    // IATA designators are two alphanumeric chars and must include at least one letter
    return /[A-Z]/.test(match[1]) ? match[1] : null;
  };

  const findCarrierIdByIata = (iata) => {
    if (!iata) return null;
    const carrier = carriersList.find((c) => c.carrier_iata && c.carrier_iata.toUpperCase() === iata.toUpperCase());
    return carrier ? carrier.carrier_id : null;
  };

  const blankManualFields = {
    mflight_route_id: null,
    mflight_carrier_id: null,
    mflight_flight_num: '',
    mflight_mcarrier_id: null,
    mflight_mflight_num: '',
    mflight_dep_date: '',
    mflight_dep_time: '',
    mflight_dep_offset: null,
    mflight_arr_date: '',
    mflight_arr_time: '',
    mflight_arr_offset: null,
    mflight_duration: null,
    mflight_distance: null,
    mflight_plane_id: null,
    mflight_plane_reg_id: null,
    mflight_plane_reg_mnl: '',
    mflight_plane_model_id: null,
    mflight_plane_model_mnl: '',
    mflight_dep_airport_id: null,
    mflight_arr_airport_id: null
  };

  const formatManualFlightLabel = (mf) => {
    const carrier = mf.carrier_iata || mf.carrier_name || '?';
    const rawNum = mf.mflight_flight_num ? String(mf.mflight_flight_num).trim() : '';
    const flightNum = (carrier && rawNum.toUpperCase().startsWith(carrier.toUpperCase()))
      ? rawNum.slice(carrier.length).trim() || rawNum
      : rawNum;
    return `${carrier} ${flightNum || '-' }: ${mf.dep_iata} → ${mf.arr_iata} (${mf.mflight_dep_date || '?'})`;
  };

  const getAvailableLegsForSchedule = (lschId) => {
    if (!lschId) {
      setAvailableLegsList([]);
      return;
    }
    
    const scheduleEntry = scheduleList.find(ls => ls.lsch_id === lschId);
    if (!scheduleEntry) {
      setAvailableLegsList([]);
      return;
    }

    // Get the operational leg from the schedule
    const operationalLegId = scheduleEntry.lsch_leg_id;
    const operationalLeg = legsList.find(l => l.leg_id === operationalLegId);
    
    if (!operationalLeg) {
      setAvailableLegsList([]);
      return;
    }

    // Find all legs that are marketing versions of this operational leg
    // (i.e., legs where leg_operational_leg_id === operationalLegId)
    const marketingLegs = legsList.filter(l => l.leg_operational_leg_id === operationalLegId);
    
    // Include the operational leg itself as the first option
    const allAvailableLegs = [operationalLeg, ...marketingLegs];
    setAvailableLegsList(allAvailableLegs);

    // Auto-select the operational leg only if no leg is currently selected AND this is a new form
    // (when creating, formData.fstat_leg_id will be null from defaults)
    // On edit, keep the existing leg_id
    if (!formData.fstat_leg_id) {
      setFormData(prev => ({ ...prev, fstat_leg_id: operationalLegId }));
    }
  };

  const isManual = !!formData.is_manual;
  const usingExistingManual = isManual && !!formData.fstat_mflight_id;
  const isFromBooking = !!formData.fstat_tsegment_id;
  const isAutomatic = !!formData.fstat_tsegment_id;

  useEffect(() => {
    if (!formData.fstat_passenger_id && preferredPassengerId) {
      setFormData((prev) => ({ ...prev, fstat_passenger_id: preferredPassengerId }));
    }
  }, [formData.fstat_passenger_id, preferredPassengerId, setFormData]);

  // Update available legs when schedule changes (user selects a schedule entry)
  useEffect(() => {
    if (formData.fstat_lsch_id && !isManual && legsList.length > 0 && scheduleList.length > 0) {
      getAvailableLegsForSchedule(formData.fstat_lsch_id);
    } else {
      // Clear available legs when switching to manual mode or when schedule is unselected
      setAvailableLegsList([]);
    }
  }, [formData.fstat_lsch_id, isManual]);

  useEffect(() => {
    loadManualFlights();
    loadSchedule();
    loadLegs();
    loadRoutes();
    loadCarriers();
    loadPlanes();
    loadAirports();
    loadPlaneRegs();
    loadPlaneModels();
    loadTimezones();
  }, []);

  const loadManualFlights = async () => {
    try {
      const response = await manualFlights.getAll();
      setManualFlightsList(response.data);
    } catch (err) {
      console.error('Failed to load manual flights', err);
    }
  };

  const loadSchedule = async () => {
    try {
      const response = await legsSchedule.getAll();
      setScheduleList(response.data);
    } catch (err) {
      console.error('Failed to load legs schedule', err);
    }
  };

  const loadLegs = async () => {
    try {
      const response = await legs.getAll();
      setLegsList(response.data);
    } catch (err) {
      console.error('Failed to load legs', err);
    }
  };

  const loadRoutes = async () => {
    try {
      const response = await routesApi.getAll();
      setRoutesList(response.data);
    } catch (err) {
      console.error('Failed to load routes', err);
    }
  };

  const loadCarriers = async () => {
    try {
      const response = await carriersApi.getAll();
      setCarriersList(response.data);
    } catch (err) {
      console.error('Failed to load carriers', err);
    }
  };

  const loadPlanes = async () => {
    try {
      const response = await planesApi.getAll();
      setPlanesList(response.data);
    } catch (err) {
      console.error('Failed to load planes', err);
    }
  };

  const loadAirports = async () => {
    try {
      const response = await airportsApi.getAll();
      setAirportsList(response.data);
    } catch (err) {
      console.error('Failed to load airports', err);
    }
  };

  const loadPlaneRegs = async () => {
    try {
      const response = await planeRegsApi.getAll();
      setPlaneRegsList(response.data);
    } catch (err) {
      console.error('Failed to load plane regs', err);
    }
  };

  const loadPlaneModels = async () => {
    try {
      const response = await planeModelsApi.getAll();
      setPlaneModelsList(response.data);
    } catch (err) {
      console.error('Failed to load plane models', err);
    }
  };

  const loadTimezones = async () => {
    try {
      const response = await timezonesApi.getAll();
      setTimezonesList(response.data);
    } catch (err) {
      console.error('Failed to load timezones', err);
    }
  };

  const getTimezoneCode = (tzId) => timezonesList.find((tz) => tz.tz_id === tzId)?.tz_code;

  const computeOffsetMinutes = (dateStr, timeStr, tzCode) => {
    if (!dateStr || !tzCode) return null;
    const safeTime = timeStr && timeStr.length ? timeStr : '00:00';
    const [year, month, day] = dateStr.split('-').map((v) => parseInt(v, 10));
    const [hour, minute] = safeTime.split(':').map((v) => parseInt(v, 10));
    if ([year, month, day].some(Number.isNaN)) return null;

    // Use UTC baseline so host timezone does not skew the result
    const baseUtcMs = Date.UTC(
      year,
      month - 1,
      day,
      Number.isNaN(hour) ? 0 : hour,
      Number.isNaN(minute) ? 0 : minute,
      0
    );
    const baseDate = new Date(baseUtcMs);
    if (Number.isNaN(baseDate.getTime())) return null;

    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tzCode,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).formatToParts(baseDate);

    const getPart = (type) => parts.find((p) => p.type === type)?.value;
    const ly = parseInt(getPart('year'), 10);
    const lm = parseInt(getPart('month'), 10);
    const ld = parseInt(getPart('day'), 10);
    const lh = parseInt(getPart('hour'), 10);
    const lmin = parseInt(getPart('minute'), 10);
    const ls = parseInt(getPart('second') || '0', 10);

    if ([ly, lm, ld, lh, lmin, ls].some(Number.isNaN)) return null;

    // Local wall time expressed as UTC milliseconds
    const localAsUtcMs = Date.UTC(ly, lm - 1, ld, lh, lmin, ls);
    const offsetMin = Math.round((localAsUtcMs - baseUtcMs) / 60000);
    return offsetMin;
  };

  // Derive airports from selected route (useful when editing existing records)
  useEffect(() => {
    if (!isManual) return;
    if (!formData.mflight_route_id) return;
    if (formData.mflight_dep_airport_id && formData.mflight_arr_airport_id) return;

    const route = routesList.find((r) => r.route_id === formData.mflight_route_id);
    if (!route) return;

    const depAirport = airportsList.find((a) => a.airport_iata_code === route.dep_iata);
    const arrAirport = airportsList.find((a) => a.airport_iata_code === route.arr_iata);

    const updates = {};
    if (!formData.mflight_dep_airport_id && depAirport) updates.mflight_dep_airport_id = depAirport.airport_id;
    if (!formData.mflight_arr_airport_id && arrAirport) updates.mflight_arr_airport_id = arrAirport.airport_id;

    if (Object.keys(updates).length > 0) {
      setFormData((prev) => ({ ...prev, ...updates }));
    }
  }, [isManual, formData.mflight_route_id, formData.mflight_dep_airport_id, formData.mflight_arr_airport_id, routesList, airportsList, setFormData]);

  // Auto-pick route based on selected airports
  useEffect(() => {
    if (!isManual) return;
    const { mflight_dep_airport_id: depId, mflight_arr_airport_id: arrId } = formData;

    if (!depId || !arrId) {
      setRouteError('');
      if (formData.mflight_route_id) {
        setFormData((prev) => ({ ...prev, mflight_route_id: null }));
      }
      return;
    }

    const depAirport = airportsList.find((a) => a.airport_id === depId);
    const arrAirport = airportsList.find((a) => a.airport_id === arrId);
    if (!depAirport || !arrAirport) return;

    const matchedRoute = routesList.find((r) => r.dep_iata === depAirport.airport_iata_code && r.arr_iata === arrAirport.airport_iata_code);
    const matchedRouteId = matchedRoute ? matchedRoute.route_id : null;

    if (matchedRouteId !== formData.mflight_route_id) {
      setFormData((prev) => ({ ...prev, mflight_route_id: matchedRouteId }));
    }

    setRouteError(matchedRouteId ? '' : 'No route found for selected airports. Create the route first in Dictionaries → Routes.');
  }, [isManual, formData.mflight_dep_airport_id, formData.mflight_arr_airport_id, formData.mflight_route_id, airportsList, routesList, setFormData]);

  useEffect(() => {
    if (!isManual) return;
    const route = routesList.find((r) => r.route_id === formData.mflight_route_id);
    const depAirport = airportsList.find((a) => a.airport_id === formData.mflight_dep_airport_id) || (route ? airportsList.find((a) => a.airport_iata_code === route.dep_iata) : null);
    const arrAirport = airportsList.find((a) => a.airport_id === formData.mflight_arr_airport_id) || (route ? airportsList.find((a) => a.airport_iata_code === route.arr_iata) : null);

    const updates = {};
    if (depAirport && formData.mflight_dep_date) {
      const tzCode = getTimezoneCode(depAirport.airport_tz_id);
      const offset = computeOffsetMinutes(formData.mflight_dep_date, formData.mflight_dep_time, tzCode);
      if (offset !== null && offset !== formData.mflight_dep_offset) updates.mflight_dep_offset = offset;
    }
    if (arrAirport && formData.mflight_arr_date) {
      const tzCode = getTimezoneCode(arrAirport.airport_tz_id);
      const offset = computeOffsetMinutes(formData.mflight_arr_date, formData.mflight_arr_time, tzCode);
      if (offset !== null && offset !== formData.mflight_arr_offset) updates.mflight_arr_offset = offset;
    }

    if (Object.keys(updates).length > 0) {
      setFormData((prev) => ({ ...prev, ...updates }));
    }
  }, [isManual, formData.mflight_route_id, formData.mflight_dep_airport_id, formData.mflight_arr_airport_id, formData.mflight_dep_date, formData.mflight_dep_time, formData.mflight_dep_offset, formData.mflight_arr_date, formData.mflight_arr_time, formData.mflight_arr_offset, routesList, airportsList, timezonesList, setFormData]);

  useEffect(() => {
    if (!isManual) return;
    const iata = extractIataFromFlightNum(formData.mflight_flight_num);
    const carrierId = findCarrierIdByIata(iata);
    if (iata && carrierId !== formData.mflight_carrier_id) {
      setFormData((prev) => ({ ...prev, mflight_carrier_id: carrierId || null }));
    }
  }, [isManual, formData.mflight_flight_num, formData.mflight_carrier_id, carriersList, setFormData]);

  useEffect(() => {
    if (!isManual) return;
    const iata = extractIataFromFlightNum(formData.mflight_mflight_num);
    const carrierId = findCarrierIdByIata(iata);
    if (iata && carrierId !== formData.mflight_mcarrier_id) {
      setFormData((prev) => ({ ...prev, mflight_mcarrier_id: carrierId || null }));
    }
  }, [isManual, formData.mflight_mflight_num, formData.mflight_mcarrier_id, carriersList, setFormData]);

  useEffect(() => {
    if (!isManual) return;
    const { mflight_dep_date, mflight_dep_time, mflight_dep_offset, mflight_arr_date, mflight_arr_time, mflight_arr_offset } = formData;
    if (!mflight_dep_date || !mflight_arr_date || mflight_dep_offset == null || mflight_arr_offset == null) return;
    if (!mflight_dep_time || !mflight_arr_time) return;
    const [depY, depM, depD] = mflight_dep_date.split('-').map((v) => parseInt(v, 10));
    const [depH, depMin] = mflight_dep_time.split(':').map((v) => parseInt(v, 10));
    const [arrY, arrM, arrD] = mflight_arr_date.split('-').map((v) => parseInt(v, 10));
    const [arrH, arrMin] = mflight_arr_time.split(':').map((v) => parseInt(v, 10));
    if ([depY, depM, depD, depH, depMin, arrY, arrM, arrD, arrH, arrMin].some(Number.isNaN)) return;

    const depUtc = Date.UTC(depY, depM - 1, depD, depH, depMin) - mflight_dep_offset * 60000;
    const arrUtc = Date.UTC(arrY, arrM - 1, arrD, arrH, arrMin) - mflight_arr_offset * 60000;
    const minutes = Math.round((arrUtc - depUtc) / 60000);
    if (!Number.isNaN(minutes)) {
      setFormData((prev) => ({ ...prev, mflight_duration: minutes }));
    }
  }, [isManual, formData.mflight_dep_date, formData.mflight_dep_time, formData.mflight_dep_offset, formData.mflight_arr_date, formData.mflight_arr_time, formData.mflight_arr_offset, setFormData]);

  // Hydrate form fields when an existing manual flight is selected so it can be edited in place
  useEffect(() => {
    if (!isManual || !formData.fstat_mflight_id) {
      if (hydratedManualId !== null) setHydratedManualId(null);
      return;
    }

    // Wait until reference data is loaded; rerun when routes/airports arrive
    if (!routesList.length || !airportsList.length || !manualFlightsList.length) return;

    if (hydratedManualId === formData.fstat_mflight_id && formData.mflight_dep_airport_id && formData.mflight_arr_airport_id) {
      return;
    }

    const manual = manualFlightsList.find((m) => m.mflight_id === formData.fstat_mflight_id);
    if (!manual) return;

    const route = routesList.find((r) => r.route_id === manual.mflight_route_id);
    const depAirport = route ? airportsList.find((a) => a.airport_iata_code === route.dep_iata) : null;
    const arrAirport = route ? airportsList.find((a) => a.airport_iata_code === route.arr_iata) : null;

    setFormData((prev) => ({
      ...prev,
      mflight_route_id: manual.mflight_route_id || null,
      mflight_carrier_id: manual.mflight_carrier_id || null,
      mflight_flight_num: manual.mflight_flight_num || '',
      mflight_mcarrier_id: manual.mflight_mcarrier_id || null,
      mflight_mflight_num: manual.mflight_mflight_num || '',
      mflight_dep_date: manual.mflight_dep_date || '',
      mflight_dep_time: manual.mflight_dep_time || '',
      mflight_dep_offset: manual.mflight_dep_offset ?? null,
      mflight_arr_date: manual.mflight_arr_date || '',
      mflight_arr_time: manual.mflight_arr_time || '',
      mflight_arr_offset: manual.mflight_arr_offset ?? null,
      mflight_duration: manual.mflight_duration ?? null,
      mflight_distance: manual.mflight_distance ?? null,
      mflight_plane_id: manual.mflight_plane_id || null,
      mflight_plane_reg_id: manual.mflight_plane_reg_id || null,
      mflight_plane_reg_mnl: manual.mflight_plane_reg_mnl || '',
      mflight_plane_model_id: manual.mflight_plane_model_id || null,
      mflight_plane_model_mnl: manual.mflight_plane_model_mnl || '',
      mflight_dep_airport_id: depAirport ? depAirport.airport_id : prev.mflight_dep_airport_id,
      mflight_arr_airport_id: arrAirport ? arrAirport.airport_id : prev.mflight_arr_airport_id
    }));

    setHydratedManualId(formData.fstat_mflight_id);
  }, [isManual, formData.fstat_mflight_id, manualFlightsList, routesList, airportsList, hydratedManualId, formData.mflight_dep_airport_id, formData.mflight_arr_airport_id, setFormData]);

  useEffect(() => {
    if (formData.fstat_mflight_id && !formData.is_manual) {
      setFormData((prev) => ({ ...prev, is_manual: true }));
    }
    if (formData.fstat_lsch_id && formData.is_manual) {
      setFormData((prev) => ({ ...prev, is_manual: false }));
    }
  }, [formData.fstat_mflight_id, formData.fstat_lsch_id, formData.is_manual, setFormData]);


  return (
    <>
      <div className="form-group">
        <label>Passenger *</label>
        <select
          value={formData.fstat_passenger_id || ''}
          onChange={(e) => setFormData({ ...formData, fstat_passenger_id: e.target.value ? parseInt(e.target.value, 10) : null })}
          required
          disabled={isFromBooking}
        >
          <option value="">Select passenger</option>
          {passengersList.map((p) => (
            <option key={p.passenger_id} value={p.passenger_id}>
              {p.passenger_name || `Passenger ${p.passenger_id}`}
              {p.passenger_is_def ? ' (you)' : ''}
            </option>
          ))}
        </select>
      </div>

      {isFromBooking && (
        <div style={{ marginBottom: '12px', padding: '12px', border: '1px solid #e0e0e0', borderRadius: '6px', background: '#fafafa' }}>
          <h4 style={{ marginTop: 0, marginBottom: '8px' }}>Booking Flight Info</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <div style={{ fontWeight: 600 }}>Route</div>
              <div>{formData.dep_iata || '?'} → {formData.arr_iata || '?'}</div>
            </div>
            <div>
              <div style={{ fontWeight: 600 }}>Flight</div>
              <div>{formData.carrier_iata || ''} {formData.flight_number || ''}</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '8px' }}>
            <div>
              <div style={{ fontWeight: 600 }}>Departure (local)</div>
              <div>
                {(formData.lsch_dep_date || '')} {(formData.lsch_dep_time || '')}
                {(() => {
                  const formatted = formatOffsetMinutes(formData.lsch_dep_offset);
                  if (formatted) return ` (${formatted})`;
                  if (formData.lsch_dep_offset_str) return ` (UTC${formData.lsch_dep_offset_str})`;
                  return '';
                })()}
              </div>
            </div>
            <div>
              <div style={{ fontWeight: 600 }}>Arrival (local)</div>
              <div>
                {(formData.lsch_arr_date || '')} {(formData.lsch_arr_time || '')}
                {(() => {
                  const formatted = formatOffsetMinutes(formData.lsch_arr_offset);
                  if (formatted) return ` (${formatted})`;
                  if (formData.lsch_arr_offset_str) return ` (UTC${formData.lsch_arr_offset_str})`;
                  return '';
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {!isFromBooking && (
        <div className="form-group" style={{ display: 'flex', gap: '12px', marginTop: '8px', alignItems: 'center' }}>
          <span style={{ fontWeight: 600 }}>Manual entry mode:</span>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: 0 }}>
            <input
              type="radio"
              name="manualMode"
              checked={!isManual}
              onChange={() => setFormData({ ...formData, is_manual: false, fstat_mflight_id: null })}
              disabled={isAutomatic}
            />
            From schedule (completed)
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: 0 }}>
            <input
              type="radio"
              name="manualMode"
              checked={isManual}
              onChange={() => setFormData({ ...formData, is_manual: true, fstat_lsch_id: null })}
              disabled={isAutomatic}
            />
            Custom manual flight
          </label>
          {isAutomatic && (
            <span style={{ color: '#e74c3c', fontSize: '0.9em', fontWeight: '600' }}>
              (Read-only - created from ticket segment)
            </span>
          )}
        </div>
      )}

      {!isManual && !isFromBooking && (
        <>
          <div className="form-group">
            <label>Schedule Entry *</label>
            <select
              value={formData.fstat_lsch_id || ''}
              onChange={(e) => {
                const lschId = e.target.value ? parseInt(e.target.value) : null;
                setFormData({ ...formData, fstat_lsch_id: lschId, fstat_leg_id: null });
                getAvailableLegsForSchedule(lschId);
              }}
              required
              disabled={isAutomatic}
            >
              <option value="">Select schedule entry</option>
              {scheduleList
                .filter(ls => ls.lsch_status === 40)
                .map(ls => (
                <option key={ls.lsch_id} value={ls.lsch_id}>
                  {ls.carrier_iata || ls.carrier_name} {ls.flight_number}: {ls.dep_iata} → {ls.arr_iata} ({ls.lsch_dep_date})
                </option>
              ))}
            </select>
          </div>

          {formData.fstat_lsch_id && availableLegsList.length > 0 && (
            <div className="form-group">
              <label>Leg *</label>
              <select
                value={formData.fstat_leg_id || ''}
                onChange={(e) => setFormData({ ...formData, fstat_leg_id: e.target.value ? parseInt(e.target.value) : null })}
              >
                <option value="">Select leg</option>
                {availableLegsList.map(leg => {
                  const isOperational = !leg.leg_operational_leg_id;
                  const label = `${leg.carrier_iata || leg.carrier_name} ${leg.flight_number}: ${leg.dep_iata} → ${leg.arr_iata}${isOperational ? ' (operational)' : ' (marketing)'}`;
                  return (
                    <option key={leg.leg_id} value={leg.leg_id}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>
          )}
        </>
      )}

      {isManual && !isFromBooking && (
        <div style={{ marginTop: '10px', padding: '12px', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
          <h4 style={{ marginTop: 0 }}>Manual Flight Details</h4>
          {usingExistingManual && (
            <div style={{ color: '#2c3e50', marginBottom: '6px' }}>
              Editing selected manual flight. Changes will update the saved manual flight record.
            </div>
          )}

          <div className="form-group">
            <label>Use Existing Manual Flight</label>
            <select
              value={formData.fstat_mflight_id || ''}
              onChange={(e) => {
                const value = e.target.value ? parseInt(e.target.value, 10) : null;
                setHydratedManualId(null);
                if (!value) {
                  setFormData({ ...formData, fstat_mflight_id: null, ...blankManualFields });
                } else {
                  setFormData({ ...formData, fstat_mflight_id: value });
                }
              }}
              disabled={isAutomatic}
            >
              <option value="">Create new from details below</option>
              {manualFlightsList.map(mf => (
                <option key={mf.mflight_id} value={mf.mflight_id}>
                  {formatManualFlightLabel(mf)}
                </option>
              ))}
            </select>
            {manualFlightsList.length === 0 && (
              <small style={{ color: '#7f8c8d' }}>No manual flights available. Fill details below to create one.</small>
            )}
          </div>

          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div className="form-group">
                <label>Departure Airport *</label>
                <select
                  value={formData.mflight_dep_airport_id || ''}
                  onChange={(e) => setFormData({ ...formData, mflight_dep_airport_id: e.target.value ? parseInt(e.target.value, 10) : null })}
                  required
                >
                  <option value="">Select departure airport</option>
                  {airportsList.map((airport) => (
                    <option key={airport.airport_id} value={airport.airport_id}>
                      {airport.airport_iata_code} ({airport.airport_icao_code})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Arrival Airport *</label>
                <select
                  value={formData.mflight_arr_airport_id || ''}
                  onChange={(e) => setFormData({ ...formData, mflight_arr_airport_id: e.target.value ? parseInt(e.target.value, 10) : null })}
                  required
                >
                  <option value="">Select arrival airport</option>
                  {airportsList.map((airport) => (
                    <option key={airport.airport_id} value={airport.airport_id}>
                      {airport.airport_iata_code} ({airport.airport_icao_code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Route (auto)</label>
              <input
                type="text"
                value={(() => {
                  const depAirport = airportsList.find((a) => a.airport_id === formData.mflight_dep_airport_id);
                  const arrAirport = airportsList.find((a) => a.airport_id === formData.mflight_arr_airport_id);
                  const route = routesList.find((r) => r.route_id === formData.mflight_route_id);
                  if (route && depAirport && arrAirport) {
                    return `${route.dep_iata} → ${route.arr_iata} (route #${route.route_id})`;
                  }
                  if (depAirport && arrAirport) {
                    return 'Route not found for selected airports';
                  }
                  return 'Select departure and arrival to auto-fill route';
                })()}
                disabled
              />
              {routeError && (
                <small style={{ color: '#c0392b' }}>{routeError}</small>
              )}
            </div>

            <div className="form-group">
              <label>Operating Flight #</label>
              <input
                type="text"
                value={formData.mflight_flight_num || ''}
                onChange={(e) => setFormData({ ...formData, mflight_flight_num: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Operating Carrier</label>
              <select
                value={formData.mflight_carrier_id || ''}
                onChange={(e) => setFormData({ ...formData, mflight_carrier_id: e.target.value ? parseInt(e.target.value) : null })}
              >
                <option value="">Unknown</option>
                {carriersList.map(c => (
                  <option key={c.carrier_id} value={c.carrier_id}>{formatCarrierLabel(c)}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Marketing Flight #</label>
              <input
                type="text"
                value={formData.mflight_mflight_num || ''}
                onChange={(e) => setFormData({ ...formData, mflight_mflight_num: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Marketing Carrier</label>
              <select
                value={formData.mflight_mcarrier_id || ''}
                onChange={(e) => setFormData({ ...formData, mflight_mcarrier_id: e.target.value ? parseInt(e.target.value) : null })}
              >
                <option value="">None</option>
                {carriersList.map(c => (
                  <option key={c.carrier_id} value={c.carrier_id}>{formatCarrierLabel(c)}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', gap: '10px' }}>
              <div className="form-group">
                <label>Departure Date</label>
                <DateInput
                  value={formData.mflight_dep_date || ''}
                  onChange={(value) => setFormData({ ...formData, mflight_dep_date: value })}
                />
              </div>
              <div className="form-group">
                <label>Departure Time</label>
                <TimeInput
                  value={formData.mflight_dep_time || ''}
                  onChange={(value) => setFormData({ ...formData, mflight_dep_time: value })}
                />
              </div>
              <div className="form-group">
                <label>Dep Offset (min)</label>
                <input
                  type="number"
                  value={formData.mflight_dep_offset ?? ''}
                  onChange={(e) => setFormData({ ...formData, mflight_dep_offset: e.target.value === '' ? null : parseInt(e.target.value) })}
                />
                <small style={{ color: '#7f8c8d' }}>{formatOffsetMinutes(formData.mflight_dep_offset) || 'UTC±HH:MM'}</small>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', gap: '10px' }}>
              <div className="form-group">
                <label>Arrival Date</label>
                <DateInput
                  value={formData.mflight_arr_date || ''}
                  onChange={(value) => setFormData({ ...formData, mflight_arr_date: value })}
                />
              </div>
              <div className="form-group">
                <label>Arrival Time</label>
                <TimeInput
                  value={formData.mflight_arr_time || ''}
                  onChange={(value) => setFormData({ ...formData, mflight_arr_time: value })}
                />
              </div>
              <div className="form-group">
                <label>Arr Offset (min)</label>
                <input
                  type="number"
                  value={formData.mflight_arr_offset ?? ''}
                  onChange={(e) => setFormData({ ...formData, mflight_arr_offset: e.target.value === '' ? null : parseInt(e.target.value) })}
                />
                <small style={{ color: '#7f8c8d' }}>{formatOffsetMinutes(formData.mflight_arr_offset) || 'UTC±HH:MM'}</small>
              </div>
            </div>

            <div className="form-group">
              <label>Duration</label>
              <TimeInput
                value={durationToTimeInput(formData.mflight_duration)}
                onChange={(value) => setFormData({ ...formData, mflight_duration: timeInputToMinutes(value) })}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div className="form-group">
                <label>Flown distance (meters)</label>
                <input
                  type="number"
                  value={formData.mflight_distance ?? ''}
                  onChange={(e) => setFormData({ ...formData, mflight_distance: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                  placeholder="optional"
                />
              </div>
              <div className="form-group">
                <label>Aircraft</label>
                <select
                  value={formData.mflight_plane_id || ''}
                  onChange={(e) => setFormData({ ...formData, mflight_plane_id: e.target.value ? parseInt(e.target.value) : null })}
                >
                  <option value="">Not specified</option>
                  {planesList.map(p => (
                    <option key={p.plane_id} value={p.plane_id}>
                      {p.plane_msn} ({p.plane_id})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Aircraft Registration (select)</label>
              <select
                value={formData.mflight_plane_reg_id || ''}
                onChange={(e) => setFormData({ ...formData, mflight_plane_reg_id: e.target.value ? parseInt(e.target.value) : null })}
              >
                <option value="">Not specified</option>
                {planeRegsList.map(reg => (
                  <option key={reg.preg_id} value={reg.preg_id}>
                    {reg.preg_value} {reg.plane_msn ? `(${reg.plane_msn})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Aircraft Reg (manual)</label>
              <input
                type="text"
                value={formData.mflight_plane_reg_mnl || ''}
                onChange={(e) => setFormData({ ...formData, mflight_plane_reg_mnl: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Aircraft Model (select)</label>
              <select
                value={formData.mflight_plane_model_id || ''}
                onChange={(e) => setFormData({ ...formData, mflight_plane_model_id: e.target.value ? parseInt(e.target.value) : null })}
              >
                <option value="">Not specified</option>
                  {planeModelsList.map(pm => {
                    const manufacturerName = toManufacturerName(pm.pmodel_manufacturer);
                    const label = manufacturerName ? `${manufacturerName} ${pm.pmodel_designation || ''}`.trim() : (pm.pmodel_designation || '');
                    return (
                      <option key={pm.pmodel_id} value={pm.pmodel_id}>
                        {label}
                      </option>
                    );
                  })}
              </select>
            </div>

            <div className="form-group">
              <label>Aircraft Model (manual)</label>
              <input
                type="text"
                value={formData.mflight_plane_model_mnl || ''}
                onChange={(e) => setFormData({ ...formData, mflight_plane_model_mnl: e.target.value })}
              />
            </div>
          </>
        </div>
      )}

      <div className="form-group">
        <label>Status *</label>
        {isFromBooking ? (
          <input
            type="text"
            value={formData.lsch_status === 20 ? 'Cancelled' : flightStatuses[formData.fstat_status]}
            disabled
          />
        ) : (
          <select
            value={formData.fstat_status || ''}
            onChange={(e) => setFormData({ ...formData, fstat_status: e.target.value ? parseInt(e.target.value) : null })}
            required
          >
            <option value="">Select status</option>
            <option value="10">Scheduled</option>
            <option value="20">Completed</option>
          </select>
        )}
      </div>

      <div className="form-group">
        <label>Cabin Type</label>
        <select
          value={formData.fstat_cabin_type || ''}
          onChange={(e) => setFormData({ ...formData, fstat_cabin_type: e.target.value ? parseInt(e.target.value) : null })}
        >
          <option value="">Not specified</option>
          <option value="10">Economy</option>
          <option value="20">Economy+</option>
          <option value="30">Business</option>
          <option value="40">First</option>
          <option value="50">Private</option>
        </select>
      </div>

      <div className="form-group">
        <label>Seat Type</label>
        <select
          value={formData.fstat_seat_type || ''}
          onChange={(e) => setFormData({ ...formData, fstat_seat_type: e.target.value ? parseInt(e.target.value) : null })}
        >
          <option value="">Not specified</option>
          <option value="10">Window</option>
          <option value="20">Middle</option>
          <option value="30">Aisle</option>
        </select>
      </div>

      <div className="form-group">
        <label>Seat Row</label>
        <input
          type="number"
          value={formData.fstat_seat_row || ''}
          onChange={(e) => setFormData({ ...formData, fstat_seat_row: e.target.value ? parseInt(e.target.value) : null })}
        />
      </div>

      <div className="form-group">
        <label>Seat Column</label>
        <input
          type="text"
          value={formData.fstat_seat_col || ''}
          onChange={(e) => setFormData({ ...formData, fstat_seat_col: e.target.value })}
          maxLength={2}
        />
      </div>

      <div className="form-group">
        <label>Reason</label>
        <select
          value={formData.fstat_reason || ''}
          onChange={(e) => setFormData({ ...formData, fstat_reason: e.target.value ? parseInt(e.target.value) : null })}
        >
          <option value="">Not specified</option>
          {Object.entries(flightReasons).map(([key, value]) => (
            <option key={key} value={key}>{value}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Note</label>
        <textarea
          value={formData.fstat_note || ''}
          onChange={(e) => setFormData({ ...formData, fstat_note: e.target.value })}
          rows={3}
        />
      </div>
    </>
  );
}

function FlightsStatList({ 
  isSharedView = false, 
  sharedPassengerId = null, 
  sharedFlightsType = null, 
  sharedUid = null,
  sharedListData = null // Новый prop с готовыми данными
}) {
  const [allLegs, setAllLegs] = useState([]);
  const [passengersList, setPassengersList] = useState([]);
  const [selectedPassengerId, setSelectedPassengerId] = useState(null);
  const [passengersError, setPassengersError] = useState('');
  const [passengersLoading, setPassengersLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Adaptive styles for shared view
  const adaptiveStyles = isSharedView ? (
    <style>{`
      /* Reduce spacing for all cells in shared view */
      .table-container table td,
      .table-container table th {
        padding-left: 5px !important;
        padding-right: 5px !important;
      }
      
      /* Reduce spacing between Route and Flight columns even more */
      .table-container table td:nth-child(3),
      .table-container table th:nth-child(3) {
        padding-right: 2px !important;
      }
      
      .table-container table td:nth-child(4),
      .table-container table th:nth-child(4) {
        padding-left: 2px !important;
      }
      
      /* Aircraft column - hide first (highest priority) */
      @media (max-width: 1500px) {
        .table-container table th:nth-child(9),
        .table-container table td:nth-child(9) {
          display: none !important;
        }
      }
      
      /* Cabin/Seat column - hide second */
      @media (max-width: 1400px) {
        .table-container table th:nth-child(10),
        .table-container table td:nth-child(10) {
          display: none !important;
        }
      }
      
      /* Distance column - hide third */
      @media (max-width: 1250px) {
        .table-container table th:nth-child(8),
        .table-container table td:nth-child(8) {
          display: none !important;
        }
      }
      
      /* Arrival time column - hide fourth */
      @media (max-width: 1100px) {
        .table-container table th:nth-child(6),
        .table-container table td:nth-child(6) {
          display: none !important;
        }
      }
      
      /* Duration column - hide fifth */
      @media (max-width: 950px) {
        .table-container table th:nth-child(7),
        .table-container table td:nth-child(7) {
          display: none !important;
        }
      }
      
      /* Dep. time column - hide last */
      @media (max-width: 800px) {
        .table-container table th:nth-child(5),
        .table-container table td:nth-child(5) {
          display: none !important;
        }
      }
    `}</style>
  ) : null;

  useEffect(() => {
    if (!isSharedView) {
      loadLegs();
      loadPassengers();
    } else {
      // In shared view, set passenger ID directly
      setSelectedPassengerId(sharedPassengerId);
      setPassengersLoading(false);
    }
  }, [isSharedView, sharedPassengerId]);

  const loadLegs = async () => {
    try {
      const response = await legs.getAll();
      setAllLegs(response.data);
    } catch (err) {
      console.error('Failed to load legs', err);
    }
  };

  const loadPassengers = async () => {
    try {
      setPassengersLoading(true);
      const response = await passengersApi.getAll();
      const list = response.data || [];
      setPassengersList(list);
      const preferred = list.find((p) => p.passenger_is_def) || list[0];
      setSelectedPassengerId(preferred ? preferred.passenger_id : null);
      setPassengersError('');
    } catch (err) {
      setPassengersError('Failed to load passengers');
      console.error('Failed to load passengers', err);
    } finally {
      setPassengersLoading(false);
    }
  };

  const getOperatedByForLegId = (legId) => {
    if (!legId) return null;
    const leg = allLegs.find(l => l.leg_id === legId);
    if (!leg || !leg.leg_operational_leg_id) return null;
    const operationalLeg = allLegs.find(l => l.leg_id === leg.leg_operational_leg_id);
    if (!operationalLeg) return null;
    return `${operationalLeg.carrier_iata || operationalLeg.carrier_name} ${operationalLeg.flight_number}`;
  };

  const getOperatorNameForLegId = (legId) => {
    if (!legId) return null;
    const leg = allLegs.find(l => l.leg_id === legId);
    if (!leg || !leg.leg_operational_leg_id) return null;
    const operationalLeg = allLegs.find(l => l.leg_id === leg.leg_operational_leg_id);
    if (!operationalLeg) return null;
    const iata = operationalLeg.carrier_iata;
    const icao = operationalLeg.carrier_icao;
    const name = operationalLeg.carrier_name;
    const display = (iata && icao) ? `${iata}/${icao}` : (iata || icao || name || '');
    return {
      display,
      name: name || '',
      iata: iata || '',
      icao: icao || ''
    };
  };

  const seatTypeCsvMap = { 10: 1, 20: 2, 30: 3 };
  const cabinTypeCsvMap = { 10: 1, 20: 1, 30: 2, 40: 2, 50: 3 };
  const reasonCsvMap = { 10: 1, 20: 2, 30: 4, 99: 4 };

  const toSeatTypeCode = (value) => seatTypeCsvMap[Number(value)] || '';
  const toCabinTypeCode = (value) => cabinTypeCsvMap[Number(value)] || '';
  const toReasonCode = (value) => reasonCsvMap[Number(value)] || '';

  const resolveFlightNumber = (item, isManual) => {
    if (isManual) return item.mflight_mflight_num || item.mflight_flight_num || '';
    const carrierPart = item.carrier_iata || item.carrier_name || '';
    const numberPart = item.flight_number || '';
    return `${carrierPart} ${numberPart}`.trim();
  };

  const resolveCarrierLabel = (item, isManual) => {
    const name = isManual
      ? (item.mflight_mcarrier_name || item.mflight_carrier_name || '')
      : (item.carrier_name || '');
    const iata = isManual
      ? (item.mflight_mcarrier_iata || item.mflight_carrier_iata || '')
      : (item.carrier_iata || '');
    const icao = isManual
      ? (item.mflight_mcarrier_icao || item.mflight_carrier_icao || '')
      : (item.carrier_icao || '');

    if (!name && !iata && !icao) return '';
    const safeIata = iata || '-';
    const safeIcao = icao || '-';
    const codePart = `${safeIata}/${safeIcao}`;
    return name ? `${name} (${codePart})` : codePart;
  };

  const resolvePlaneTypeLabel = (item, isManual) => {
    const model = isManual
      ? (item.mflight_plane_model_auto || item.mflight_plane_model || item.mflight_plane_model_mnl)
      : item.plane_model;
    const manufacturer = isManual
      ? toManufacturerName(item.mflight_plane_model_manufacturer_auto || item.mflight_plane_model_manufacturer || '')
      : toManufacturerName(item.plane_model_manufacturer || '');
    const iata = isManual
      ? (item.mflight_plane_model_iata_auto || item.mflight_plane_model_iata)
      : item.plane_model_iata;
    const icao = isManual
      ? (item.mflight_plane_model_icao_auto || item.mflight_plane_model_icao)
      : item.plane_model_icao;

    const fullName = manufacturer ? `${manufacturer} ${model || ''}`.trim() : (model || '');
    if (!fullName && !iata && !icao) return '';
    const safeIata = iata || '-';
    const safeIcao = icao || '-';
    const codePart = `${safeIcao}/${safeIata}`;
    return fullName ? `${fullName} (${codePart})` : codePart;
  };

  const resolveModelDisplay = (item, isManual, hasManualAircraft) => {
    const model = isManual
      ? (hasManualAircraft
          ? (item.mflight_plane_model_auto || item.mflight_plane_model || item.mflight_plane_model_mnl)
          : (item.mflight_plane_model || item.mflight_plane_model_mnl))
      : item.plane_model;
    const manufacturer = isManual
      ? (hasManualAircraft
          ? toManufacturerName(item.mflight_plane_model_manufacturer_auto || item.mflight_plane_model_manufacturer || '')
          : toManufacturerName(item.mflight_plane_model_manufacturer || ''))
      : toManufacturerName(item.plane_model_manufacturer || '');

    if (!model && !manufacturer) return '';
    return manufacturer ? `${manufacturer} ${model || ''}`.trim() : model;
  };

  const resolveRegNumber = (item, isManual) => {
    const hasManualAircraft = isManual && item.mflight_plane_id;
    const regValue = isManual
      ? (hasManualAircraft
          ? (item.mflight_plane_reg_auto || item.mflight_plane_reg || item.mflight_plane_reg_mnl)
          : (item.mflight_plane_reg || item.mflight_plane_reg_mnl))
      : item.plane_reg;
    const msnValue = isManual
      ? (hasManualAircraft ? item.mflight_plane_msn : null)
      : item.plane_msn;

    return regValue || msnValue || '';
  };

  const formatDurationForCsv = (minutes) => {
    if (minutes === null || minutes === undefined || Number.isNaN(minutes)) return '';
    return formatDurationHM(minutes);
  };

  const buildSeatNumber = (row, col) => {
    if (!row && !col) return '';
    return `${row || ''}${col || ''}`.trim();
  };

  const escapeCsv = (value) => {
    if (value === null || value === undefined) return '';
    const str = String(value).replace(/"/g, '""');
    return /[",\n]/.test(str) ? `"${str}"` : str;
  };

  const handleDownloadCsv = async () => {
    if (!selectedPassengerId) {
      alert('Please select a passenger first.');
      return;
    }

    setExporting(true);
    try {
      const response = await flightsStat.getAll({ passenger_id: selectedPassengerId });
      const rows = response.data || [];
      const header = [
        'Departure Date',
        'Departure Time',
        'Flight Number',
        'Departure Airport',
        'Arrival Airport',
        'Arrival Date',
        'Arrival Time',
        'Flight Duration',
        'Airline (IATA/ICAO)',
        'Aircraft Type (ICAO/IATA)',
        'Registration Number',
        'Seat Type (1-Window,2-Middle,3-Aisle)',
        'Cabin Type (1-Economy,2-Business)',
        'Travel Purpose (1-Leisure,2-Business,4-Other)',
        'Seat Number'
      ];

      const body = rows
        .filter((item) => item.fstat_status === 20)
        .map((item) => {
        const isManual = item.is_manual;
        const depDate = isManual ? item.mflight_dep_date : item.lsch_dep_date;
        const depTime = isManual ? item.mflight_dep_time : item.lsch_dep_time;
        const arrDate = isManual ? item.mflight_arr_date : item.lsch_arr_date;
        const arrTime = isManual ? item.mflight_arr_time : item.lsch_arr_time;
        const durationMin = isManual ? item.mflight_duration : item.lsch_duration;
        const depAirport = isManual ? item.mflight_dep_iata : item.dep_iata;
        const arrAirport = isManual ? item.mflight_arr_iata : item.arr_iata;
        const seatNumber = buildSeatNumber(item.fstat_seat_row, item.fstat_seat_col);

        const rowValues = [
          formatDate(depDate),
          formatTime(depTime),
          resolveFlightNumber(item, isManual),
          depAirport || '',
          arrAirport || '',
          formatDate(arrDate),
          formatTime(arrTime),
          formatDurationForCsv(durationMin),
          resolveCarrierLabel(item, isManual),
          resolvePlaneTypeLabel(item, isManual),
          resolveRegNumber(item, isManual),
          toSeatTypeCode(item.fstat_seat_type),
          toCabinTypeCode(item.fstat_cabin_type),
          toReasonCode(item.fstat_reason),
          seatNumber
        ];

        return rowValues.map(escapeCsv).join(',');
      });

      const csvContent = [header.join(','), ...body].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'flights.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export flights CSV', err);
      alert('Failed to generate CSV. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  if (passengersLoading) {
    return <div className="loading">Loading passengers...</div>;
  }

  if (passengersError) {
    return <div className="error">{passengersError}</div>;
  }

  // Skip passenger check in shared view
  if (!isSharedView && (!passengersList.length || !selectedPassengerId)) {
    return <div className="error">No passengers found. Please create a passenger first.</div>;
  }

  const preferredPassengerId = selectedPassengerId;
  const formDefaults = {
    fstat_passenger_id: preferredPassengerId,
    fstat_leg_type: null,
    fstat_lsch_id: null,
    fstat_tsegment_id: null,
    fstat_leg_id: null,
    fstat_mflight_id: null,
    is_manual: true,
    fstat_status: 10,
    fstat_cabin_type: null,
    fstat_seat_type: null,
    fstat_seat_row: null,
    fstat_seat_col: '',
    fstat_reason: null,
    fstat_note: '',
    dep_iata: '',
    arr_iata: '',
    carrier_iata: '',
    flight_number: '',
    lsch_dep_date: '',
    lsch_dep_time: '',
    lsch_arr_date: '',
    lsch_arr_time: '',
    lsch_dep_offset: null,
    lsch_arr_offset: null,
    lsch_dep_offset_str: '',
    lsch_arr_offset_str: '',
    mflight_route_id: null,
    mflight_carrier_id: null,
    mflight_flight_num: '',
    mflight_mcarrier_id: null,
    mflight_mflight_num: '',
    mflight_dep_date: '',
    mflight_dep_time: '',
    mflight_dep_offset: null,
    mflight_arr_date: '',
    mflight_arr_time: '',
    mflight_arr_offset: null,
    mflight_duration: null,
    mflight_distance: null,
    mflight_plane_id: null,
    mflight_plane_reg_id: null,
    mflight_plane_reg_mnl: '',
    mflight_plane_model_id: null,
    mflight_plane_model_mnl: '',
    mflight_dep_airport_id: null,
    mflight_arr_airport_id: null
  };

  // Render row for unified shared view data format
  const renderSharedRow = (item) => {
    const isCompleted = item.flightStatus === 'completed';
    const isManual = item.recordType === 'manual';

    // Format delay in minutes to text
    const formatDelayText = (delayMin) => {
      if (delayMin === null || delayMin === undefined) return null;
      if (delayMin === 0) return 'on time';
      if (delayMin > 0) return `delayed ${delayMin}m`;
      return `early ${Math.abs(delayMin)}m`;
    };

    // Format duration diff in minutes to text
    const formatDurationDiffText = (diffMin) => {
      if (diffMin === null || diffMin === undefined || diffMin === 0) return null;
      const isSlower = diffMin > 0;
      const total = Math.abs(diffMin);
      const h = Math.floor(total / 60);
      const m = total % 60;
      let timeStr;
      if (h === 0) timeStr = `${m}m`;
      else if (m === 0) timeStr = `${h}h`;
      else timeStr = `${h}h ${m}m`;
      return isSlower ? `slower by ${timeStr}` : `faster by ${timeStr}`;
    };

    return (
      <>
        <td style={{ textAlign: 'center', paddingLeft: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <FlightStatusIcon isCancelled={item.lsch_status === 20} isCompleted={isCompleted} />
            {isManual && (
              <MobileTooltip title="Added manually">
                <span style={{ fontSize: '0.9em', color: '#7f8c8d' }}>📝</span>
              </MobileTooltip>
            )}
          </div>
        </td>
        <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
          <strong>
            {(() => {
              if (!item.depDate) return '-';
              const [year, month, day] = item.depDate.split('-');
              return `${day}.${month}.${year}`;
            })()}
          </strong>
        </td>
        <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px', minHeight: '32px' }}>
            <div style={{ flex: '1', whiteSpace: 'nowrap' }}>
              {item.actualRoute ? (
                <div>
                  <div style={{ textDecoration: 'line-through', color: '#7f8c8d' }}>
                    {item.plannedRoute.dep} → {item.plannedRoute.arr}
                  </div>
                  <div>
                    <strong>
                      {item.actualRoute.dep} → {item.actualRoute.arr}
                    </strong>
                  </div>
                </div>
              ) : (
                <strong>{item.plannedRoute.dep} → {item.plannedRoute.arr}</strong>
              )}
            </div>
            <div style={{ flex: '0 0 auto', minWidth: '50px' }}>
              {item.flightType && (
                <MobileTooltip title={item.flightType === 'international' ? 'International' : 'Domestic'}>
                  <span
                    className={`badge ${item.flightType === 'domestic' ? 'badge-info' : 'badge-success'}`}
                    style={item.flightType === 'international' ? { color: '#145a32', background: 'rgba(46, 204, 113, 0.18)', border: '1px solid #2ecc71', minWidth: '26px', display: 'inline-block', textAlign: 'center' } : { color: '#004085', background: 'rgba(0, 123, 255, 0.15)', border: '1px solid #0078d4', minWidth: '26px', display: 'inline-block', textAlign: 'center' }}
                  >
                    {item.flightType === 'international' ? 'I' : 'D'}
                  </span>
                </MobileTooltip>
              )}
            </div>
          </div>
        </td>
        <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
          {(() => {
            // Display marketing carrier if present, otherwise operating carrier
            const displayCarrier = item.marketingCarrier || item.carrier;
            const operatingCarrier = item.marketingCarrier ? item.carrier : null;
            const flightNum = item.marketingFlightNumber || item.flightNumber || '-';
            
            // Build carrier tooltip
            const buildCarrierTooltip = (carrier) => {
              if (!carrier) return '';
              const iata = carrier.iata_code;
              const icao = carrier.icao_code;
              const name = carrier.name;
              const codes = (iata && icao) ? ` (${iata}/${icao})` : (iata || icao ? ` (${iata || icao})` : '');
              return name ? `${name}${codes}` : '';
            };
            
            return (
              <>
                <MobileTooltip title={buildCarrierTooltip(displayCarrier)}>
                  <strong>{flightNum}</strong>
                </MobileTooltip>
                {operatingCarrier && (
                  <div style={{ fontSize: '0.85em', color: '#7f8c8d', marginTop: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <MobileTooltip title={buildCarrierTooltip(operatingCarrier)}>
                      <strong>{item.flightNumber}</strong>
                    </MobileTooltip>
                    <MobileTooltip title={`Operated as ${item.flightNumber} (${operatingCarrier.name || ''})`}>
                      <span style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(127, 140, 141, 0.2)',
                        color: '#7f8c8d',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}>
                        ?
                      </span>
                    </MobileTooltip>
                  </div>
                )}
              </>
            );
          })()}
        </td>
        <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
          {item.depTimeActual ? (
            item.depDelay === 0 ? (
              <div>
                {item.depTimePlanned}
                <div style={{ fontSize: '0.75em', color: '#27ae60' }}>
                  (on time)
                </div>
              </div>
            ) : (
              <>
                <div>
                  <span style={{ textDecoration: 'line-through' }}>{item.depTimePlanned}</span>
                  <span style={{ marginLeft: '8px', color: item.depDelay > 0 ? '#c0392b' : '#27ae60' }}>{item.depTimeActual}</span>
                </div>
                {item.depDelay != null && (
                  <div style={{ fontSize: '0.75em', color: item.depDelay > 0 ? '#c0392b' : '#27ae60' }}>
                    ({formatDelayText(item.depDelay)})
                  </div>
                )}
              </>
            )
          ) : (
            <div>
              {item.depTimePlanned || '-'}
            </div>
          )}
        </td>
        <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
          {item.arrTimeActual ? (
            item.arrDelay === 0 ? (
              <div>
                {item.arrTimePlanned}
                {item.arrDayDiff && <span> (+{item.arrDayDiff})</span>}
                <div style={{ fontSize: '0.75em', color: '#27ae60' }}>
                  (on time)
                </div>
              </div>
            ) : (
              <>
                <div>
                  <span style={{ textDecoration: 'line-through' }}>{item.arrTimePlanned}</span>
                  <span style={{ marginLeft: '8px', color: item.arrDelay > 0 ? '#c0392b' : '#27ae60' }}>{item.arrTimeActual}</span>
                  {item.arrDayDiff && <span style={{ marginLeft: '4px' }}>(+{item.arrDayDiff})</span>}
                </div>
                {item.arrDelay != null && (
                  <div style={{ fontSize: '0.75em', color: item.arrDelay > 0 ? '#c0392b' : '#27ae60' }}>
                    ({formatDelayText(item.arrDelay)})
                  </div>
                )}
              </>
            )
          ) : (
            <div>
              {item.arrTimePlanned || '-'}
              {item.arrDayDiff && <span> (+{item.arrDayDiff})</span>}
            </div>
          )}
        </td>
        <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
          {item.durationActual && item.durationActual !== item.durationPlanned ? (
            <div>
              <div>
                <span style={{ textDecoration: 'line-through' }}>{item.durationPlanned}</span>
                <span style={{ marginLeft: '8px', color: item.durationDiff > 0 ? '#c0392b' : '#27ae60' }}>{item.durationActual}</span>
              </div>
              {item.durationDiff != null && (
                <div style={{ fontSize: '0.75em', color: item.durationDiff > 0 ? '#c0392b' : '#27ae60' }}>
                  ({formatDurationDiffText(item.durationDiff)})
                </div>
              )}
            </div>
          ) : (
            item.durationPlanned || '-'
          )}
        </td>
        <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
          {item.actualRoute && item.distanceActual != null ? (
            <div>
              <div style={{ textDecoration: 'line-through', color: '#7f8c8d' }}>
                {item.distancePlanned != null ? `${item.distancePlanned} km` : '-'}
              </div>
              <div>{item.distanceActual} km</div>
            </div>
          ) : item.distanceActual != null && item.distancePlanned !== item.distanceActual ? (
            <MobileTooltip title={`Actual: ${item.distanceActual} km`}>
              <div style={{ cursor: 'help' }}>
                {item.distancePlanned != null ? `${item.distancePlanned} km` : '-'}
                <span style={{ 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(127, 140, 141, 0.2)',
                  color: '#7f8c8d',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  marginLeft: '4px',
                  cursor: 'pointer'
                }}>
                  ?
                </span>
              </div>
            </MobileTooltip>
          ) : (
            item.distancePlanned != null ? `${item.distancePlanned} km` : '-'
          )}
        </td>
        <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
          {(item.planeReg || item.planeModel) ? (
            <>
              {item.planeReg && <strong>{item.planeReg}</strong>}
              {item.planeModel && (
                <div style={{ fontSize: '0.85em', color: '#7f8c8d' }}>{item.planeModel}</div>
              )}
            </>
          ) : '-'}
        </td>
        <td style={{ textAlign: 'center', whiteSpace: 'nowrap', paddingRight: '12px' }}>
          <div>{item.cabinType ? item.cabinType.charAt(0).toUpperCase() + item.cabinType.slice(1) : '-'}</div>
          <div style={{ fontSize: '0.85em', color: '#7f8c8d', marginTop: '2px' }}>
            {item.seatNumber ? (
              <>
                <strong>{item.seatNumber}</strong>
                {item.seatType && ` (${item.seatType.charAt(0).toUpperCase() + item.seatType.slice(1)})`}
              </>
            ) : '-'}
          </div>
        </td>
      </>
    );
  };

  return (
    <div style={isSharedView ? { maxWidth: '1300px', margin: '0 auto' } : {}}>
      {adaptiveStyles}
      <CRUDList
        title="Flights List"
        api={isSharedView && sharedListData ? {
          getAll: async () => {
            // Используем готовые данные вместо запроса
            return { data: sharedListData };
          }
        } : flightsStat}
        columns={['', 'Date', 'Route', 'Flight', 'Dep. time', 'Arr. time', 'Duration', 'Distance', 'Aircraft', 'Cabin / Seat']}
        FormComponent={!isSharedView ? FlightStatForm : null}
        formProps={{ passengersList, preferredPassengerId }}
        defaultFormData={formDefaults}
        queryParams={!isSharedView ? { passenger_id: selectedPassengerId } : {}}
        disableDelete={(item) => isSharedView || !!item.fstat_tsegment_id}
        disableCreate={isSharedView}
        disableEdit={isSharedView}
        filterComponent={!isSharedView && (
          <label style={{ margin: 0, color: '#2c3e50' }}>
            Passenger:
            <select
              style={{ marginLeft: '8px' }}
              value={selectedPassengerId || ''}
              onChange={(e) => setSelectedPassengerId(e.target.value ? parseInt(e.target.value, 10) : null)}
            >
              {passengersList.map((p) => (
                <option key={p.passenger_id} value={p.passenger_id}>
                  {p.passenger_name || `Passenger ${p.passenger_id}`}
                  {p.passenger_is_def ? ' (you)' : ''}
                </option>
              ))}
            </select>
          </label>
        )}
        extraActions={!isSharedView && (
          <button
            className="btn btn-secondary"
            onClick={handleDownloadCsv}
            disabled={exporting}
            title="Download CSV of flights"
          >
            {exporting ? 'Generating…' : 'Download CSV'}
          </button>
        )}
        getRowStyle={(item) => {
          // Shared view uses flightStatus field, normal view uses fstat_status
          if (isSharedView) {
            return {
              backgroundColor: item.lsch_status === 20
                ? '#fff1f0'
                : item.flightStatus === 'planned' ? 'rgba(52, 152, 219, 0.1)' : 'transparent'
            };
          }
          return {
            backgroundColor: !item.is_manual && item.lsch_status === 20
              ? '#fff1f0'
              : item.fstat_status === 10 ? 'rgba(52, 152, 219, 0.1)' : 'transparent'
          };
        }}
        renderRow={isSharedView ? renderSharedRow : (item) => {
        const isManual = item.is_manual;
        const isScheduleCancelled = !isManual && item.lsch_status === 20;
        const durationMin = isManual ? item.mflight_duration : item.lsch_duration;
        const isFutureFlight = !isManual && item.lflight_dep_utc_min === null;
        const plannedDistance = isManual ? item.mflight_apair_distance : item.apair_distance;
        const actualDistance = isManual ? item.mflight_distance : item.lflight_distance;
        const displayDistance = plannedDistance ?? actualDistance;
        const hasManualAircraft = isManual && item.mflight_plane_id;
        const regValue = isManual
          ? (hasManualAircraft
              ? (item.mflight_plane_reg_auto || item.mflight_plane_reg || item.mflight_plane_reg_mnl)
              : (item.mflight_plane_reg || item.mflight_plane_reg_mnl))
          : item.plane_reg;
        const msnValue = isManual
          ? (hasManualAircraft ? item.mflight_plane_msn : null)
          : item.plane_msn;
        const modelValue = isManual
          ? (hasManualAircraft
              ? (item.mflight_plane_model_auto || item.mflight_plane_model || item.mflight_plane_model_mnl)
              : (item.mflight_plane_model || item.mflight_plane_model_mnl))
          : item.plane_model;
        const regDisplay = regValue || msnValue;
        const modelDisplay = resolveModelDisplay(item, isManual, hasManualAircraft);

        return (
          <>
            <td style={{ textAlign: 'center', paddingLeft: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <FlightStatusIcon isCancelled={isScheduleCancelled} isCompleted={item.fstat_status === 20} />
                {isManual && (
                  <MobileTooltip title="Added manually">
                    <span style={{ fontSize: '0.9em', color: '#7f8c8d' }}>📝</span>
                  </MobileTooltip>
                )}
              </div>
            </td>
            <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
              <strong>
                {(() => {
                  const date = isManual ? item.mflight_dep_date : item.lsch_dep_date;
                  if (!date) return '-';
                  const [year, month, day] = date.split('-');
                  return `${day}.${month}.${year}`;
                })()}
              </strong>
            </td>
            <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', minHeight: '32px' }}>
                <div style={{ flex: '1', whiteSpace: 'nowrap' }}>
                  {(() => {
                    if (isManual) {
                      return <strong>{item.mflight_dep_iata} → {item.mflight_arr_iata}</strong>;
                    }
                    
                    // Check if completed flight has different actual route
                    const isCompleted = item.fstat_status === 20;
                    const hasActualRoute = isCompleted && item.lflight_dep_iata && item.lflight_arr_iata;
                    const routeMismatch = hasActualRoute && 
                      (item.dep_iata !== item.lflight_dep_iata || item.arr_iata !== item.lflight_arr_iata);
                    
                    if (routeMismatch) {
                      return (
                        <div>
                          <div style={{ textDecoration: 'line-through', color: '#7f8c8d' }}>
                            {item.dep_iata} → {item.arr_iata}
                          </div>
                          <div>
                            <strong>
                              {item.lflight_dep_iata} → {item.lflight_arr_iata}
                            </strong>
                          </div>
                        </div>
                      );
                    }
                    
                    return <strong>{item.dep_iata} → {item.arr_iata}</strong>;
                  })()}
                </div>
                <div style={{ flex: '0 0 auto', minWidth: '50px' }}>
                  {item.fstat_leg_type && (
                    <MobileTooltip title={item.fstat_leg_type === 2 ? 'International' : 'Domestic'}>
                      <span
                        className={`badge ${item.fstat_leg_type === 1 ? 'badge-info' : 'badge-success'}`}
                        style={item.fstat_leg_type === 2 ? { color: '#145a32', background: 'rgba(46, 204, 113, 0.18)', border: '1px solid #2ecc71', minWidth: '26px', display: 'inline-block', textAlign: 'center' } : { color: '#004085', background: 'rgba(0, 123, 255, 0.15)', border: '1px solid #0078d4', minWidth: '26px', display: 'inline-block', textAlign: 'center' }}
                      >
                        {item.fstat_leg_type === 2 ? 'I' : 'D'}
                      </span>
                    </MobileTooltip>
                  )}
                </div>
              </div>
            </td>
            <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
              {isManual ? (
                <>
                  <MobileTooltip title={(() => {
                    const mainIata = item.mflight_mcarrier_iata || item.mflight_carrier_iata;
                    const mainIcao = item.mflight_mcarrier_icao || item.mflight_carrier_icao;
                    const mainName = item.mflight_mcarrier_name || item.mflight_carrier_name;
                    const codes = mainIata || mainIcao ? ` (${mainIata || mainIcao}/${mainIcao || mainIata})` : '';
                    return mainName ? `${mainName}${codes}` : '';
                  })()}>
                    <strong>{item.mflight_mflight_num || item.mflight_flight_num || '-'}</strong>
                  </MobileTooltip>
                  {item.mflight_mflight_num && item.mflight_flight_num && (
                    <div style={{ fontSize: '0.85em', color: '#7f8c8d', marginTop: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                      {item.mflight_flight_num}
                      <MobileTooltip title={`Operated as ${item.mflight_flight_num} (${item.mflight_carrier_name || item.mflight_carrier_iata})`}>
                        <span style={{ 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          backgroundColor: 'rgba(127, 140, 141, 0.2)',
                          color: '#7f8c8d',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          cursor: 'pointer'
                        }}>
                          ?
                        </span>
                      </MobileTooltip>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <MobileTooltip title={(() => {
                    const iata = item.carrier_iata;
                    const icao = item.carrier_icao;
                    const name = item.carrier_name;
                    const codes = iata || icao ? ` (${iata || icao}/${icao || iata})` : '';
                    return name ? `${name}${codes}` : '';
                  })()}>
                    <strong>{item.carrier_iata || item.carrier_name} {item.flight_number}</strong>
                  </MobileTooltip>
                    {getOperatedByForLegId(item.leg_id) && (
                      <div style={{ fontSize: '0.85em', color: '#7f8c8d', marginTop: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                        <MobileTooltip title={(() => {
                          const operatorInfo = getOperatorNameForLegId(item.leg_id);
                          if (!operatorInfo) return '';
                          const iata = operatorInfo.iata;
                          const icao = operatorInfo.icao;
                          const name = operatorInfo.name;
                          const codes = (iata && icao) ? ` (${iata}/${icao})` : (iata || icao ? ` (${iata || icao})` : '');
                          return name ? `${name}${codes}` : '';
                        })()}>
                          <strong>
                            {getOperatedByForLegId(item.leg_id)}
                          </strong>
                        </MobileTooltip>
                        <span style={{ 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          backgroundColor: 'rgba(127, 140, 141, 0.2)',
                          color: '#7f8c8d',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          cursor: 'pointer'
                        }}>
                          <MobileTooltip title={`Operated as ${getOperatedByForLegId(item.leg_id)} (${getOperatorNameForLegId(item.leg_id)?.name || ''})`}>
                            <span>?</span>
                          </MobileTooltip>
                        </span>
                      </div>
                    )}
                </>
              )}
            </td>
            <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
              {!isManual && item.lflight_dep_utc_min !== null && item.lsch_dep_utc_min !== null ? (
                (() => {
                  const schedDepLocal = item.lsch_dep_utc_min + (item.lsch_dep_offset || 0);
                  const actualDepLocal = item.lflight_dep_utc_min + (item.lflight_dep_offset || 0);
                  const depDelay = actualDepLocal - schedDepLocal;
                  const depColor = depDelay > 0 ? '#c0392b' : '#27ae60';
                  if (schedDepLocal !== actualDepLocal) {
                    const totalMinutes = ((actualDepLocal % 1440) + 1440) % 1440;
                    const depHours = Math.floor(totalMinutes / 60);
                    const depMinutes = totalMinutes % 60;
                    const actualDepTime = `${String(depHours).padStart(2, '0')}:${String(depMinutes).padStart(2, '0')}`;
                    const schedDepTime = `${String(Math.floor(schedDepLocal / 60) % 24).padStart(2, '0')}:${String(schedDepLocal % 60).padStart(2, '0')}`;
                    return (
                      <>
                        <div>
                          <span style={{ textDecoration: 'line-through' }}>{schedDepTime}</span>
                          <span style={{ marginLeft: '8px', color: depColor }}>{actualDepTime}</span>
                        </div>
                        {(() => {
                          const depInfo = formatDelay(depDelay);
                          return (
                            <div style={{ fontSize: '0.75em', color: depInfo.color }}>
                              ({depInfo.text})
                            </div>
                          );
                        })()}
                      </>
                    );
                  }
                  return (
                    <div>
                      {item.lsch_dep_time}
                      <div style={{ fontSize: '0.75em', color: '#7f8c8d' }}>
                        (on time)
                      </div>
                    </div>
                  );
                })()
              ) : (
                isManual ? item.mflight_dep_time : item.lsch_dep_time
              )}
            </td>
            <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
              {!isManual && item.lflight_arr_utc_min !== null && item.lsch_arr_utc_min !== null ? (
                (() => {
                  const schedDepLocal = item.lsch_dep_utc_min + (item.lsch_dep_offset || 0);
                  const schedArrLocal = item.lsch_arr_utc_min + (item.lsch_arr_offset || 0);
                  const actualArrLocal = item.lflight_arr_utc_min + (item.lflight_arr_offset || 0);
                  const arrDelay = actualArrLocal - schedArrLocal;
                  const arrColor = arrDelay > 0 ? '#c0392b' : '#27ae60';
                  
                  let dayDiff = 0;
                  if (item.lsch_dep_date && item.lsch_arr_date && item.lsch_dep_date !== item.lsch_arr_date) {
                    const depDate = new Date(item.lsch_dep_date);
                    const arrDate = new Date(item.lsch_arr_date);
                    dayDiff = Math.round((arrDate - depDate) / (1000 * 60 * 60 * 24));
                  }
                  
                  if (schedArrLocal !== actualArrLocal) {
                    const totalMinutes = ((actualArrLocal % 1440) + 1440) % 1440;
                    const arrHours = Math.floor(totalMinutes / 60);
                    const arrMinutes = totalMinutes % 60;
                    const actualArrTime = `${String(arrHours).padStart(2, '0')}:${String(arrMinutes).padStart(2, '0')}`;
                    const schedArrTime = `${String(Math.floor(schedArrLocal / 60) % 24).padStart(2, '0')}:${String(schedArrLocal % 60).padStart(2, '0')}`;
                    return (
                      <>
                        <div>
                          <span style={{ textDecoration: 'line-through' }}>{schedArrTime}</span>
                          <span style={{ marginLeft: '8px', color: arrColor }}>{actualArrTime}</span>
                          {dayDiff > 0 && <span style={{ marginLeft: '4px' }}>(+{dayDiff})</span>}
                        </div>
                        {(() => {
                          const arrInfo = formatDelay(arrDelay);
                          return (
                            <div style={{ fontSize: '0.75em', color: arrInfo.color }}>
                              ({arrInfo.text})
                            </div>
                          );
                        })()}
                      </>
                    );
                  }
                  return (
                    <div>
                      {item.lsch_arr_time}
                      {dayDiff > 0 && <span> (+{dayDiff})</span>}
                      <div style={{ fontSize: '0.75em', color: '#7f8c8d' }}>
                        (on time)
                      </div>
                    </div>
                  );
                })()
              ) : (
                <div>
                  {isManual ? item.mflight_arr_time : item.lsch_arr_time}
                  {(() => {
                    if (isManual) {
                      if (item.mflight_dep_date && item.mflight_arr_date) {
                        const depDate = new Date(item.mflight_dep_date);
                        const arrDate = new Date(item.mflight_arr_date);
                        const dayDiff = Math.round((arrDate - depDate) / (1000 * 60 * 60 * 24));
                        return dayDiff > 0 ? <span> (+{dayDiff})</span> : null;
                      }
                      return null;
                    }
                    if (item.lsch_dep_date && item.lsch_arr_date && item.lsch_dep_date !== item.lsch_arr_date) {
                      const depDate = new Date(item.lsch_dep_date);
                      const arrDate = new Date(item.lsch_arr_date);
                      const dayDiff = Math.round((arrDate - depDate) / (1000 * 60 * 60 * 24));
                      return dayDiff > 0 ? <span> (+{dayDiff})</span> : null;
                    }
                    return null;
                  })()}
                </div>
              )}
            </td>
            <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
              {(item.lflight_dep_utc_min !== null && item.lflight_arr_utc_min !== null) ? (
                (() => {
                  const schedDepLocal = item.lsch_dep_utc_min + (item.lsch_dep_offset || 0);
                  const actualDepLocal = item.lflight_dep_utc_min + (item.lflight_dep_offset || 0);
                  const depDelay = actualDepLocal - schedDepLocal;
                  
                  const schedArrLocal = item.lsch_arr_utc_min + (item.lsch_arr_offset || 0);
                  const actualArrLocal = item.lflight_arr_utc_min + (item.lflight_arr_offset || 0);
                  const arrDelay = actualArrLocal - schedArrLocal;
                  
                  // Calculate actual duration from actual departure and arrival times
                  const actualDurationMin = item.lflight_arr_utc_min - item.lflight_dep_utc_min;
                  const durationDiff = actualDurationMin - durationMin;
                  const durationColor = durationDiff > 0 ? '#c0392b' : '#27ae60';
                  
                  if (durationDiff !== 0) {
                    return (
                      <div>
                        <div>
                          <span style={{ textDecoration: 'line-through' }}>{formatDurationHM(durationMin)}</span>
                          <span style={{ marginLeft: '8px', color: durationColor }}>{formatDurationHM(actualDurationMin)}</span>
                        </div>
                        {(() => {
                          const diffDesc = formatDurationDiffDesc(durationDiff);
                          return diffDesc ? (
                            <div style={{ fontSize: '0.75em', color: durationColor }}>
                              ({diffDesc})
                            </div>
                          ) : null;
                        })()}
                      </div>
                    );
                  }
                  return formatDurationHM(durationMin);
                })()
              ) : (
                formatDurationHM(durationMin)
              )}
            </td>
            <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
              {(() => {
                if (isManual) {
                  // Manual flights - show as before with tooltip
                  return (
                    <MobileTooltip title={actualDistance ? `Actual: ${formatDistance(actualDistance)}` : ''}>
                      <div style={{ cursor: actualDistance ? 'help' : 'auto' }}>
                        {formatDistance(displayDistance)}
                        {actualDistance && (
                          <span style={{ 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          backgroundColor: 'rgba(127, 140, 141, 0.2)',
                          color: '#7f8c8d',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          marginLeft: '4px',
                          cursor: 'pointer'
                        }}>
                          ?
                        </span>
                      )}
                      </div>
                    </MobileTooltip>
                  );
                }
                
                // Schedule-based flights - check for route change
                const isCompleted = item.fstat_status === 20;
                const hasActualRoute = isCompleted && item.lflight_dep_iata && item.lflight_arr_iata;
                const routeMismatch = hasActualRoute && 
                  (item.dep_iata !== item.lflight_dep_iata || item.arr_iata !== item.lflight_arr_iata);
                const actualRouteDistance = item.lflight_apair_distance;
                
                // If route changed, show both planned and actual distances
                if (routeMismatch && actualRouteDistance != null) {
                  return (
                    <div>
                      <div style={{ textDecoration: 'line-through', color: '#7f8c8d' }}>
                        {formatDistance(plannedDistance)}
                      </div>
                      <div>
                        {formatDistance(actualDistance || actualRouteDistance)}
                      </div>
                    </div>
                  );
                }
                
                // Normal display with tooltip for actual distance
                return (
                  <MobileTooltip title={actualDistance ? `Actual: ${formatDistance(actualDistance)}` : ''}>
                    <div style={{ cursor: actualDistance ? 'help' : 'auto' }}>
                      {formatDistance(displayDistance)}
                      {actualDistance && plannedDistance !== actualDistance && (
                        <span style={{ 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          backgroundColor: 'rgba(127, 140, 141, 0.2)',
                          color: '#7f8c8d',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          marginLeft: '4px',
                          cursor: 'pointer'
                        }}>
                          ?
                        </span>
                      )}
                    </div>
                  </MobileTooltip>
                );
              })()}
            </td>
            <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
              {(regDisplay || modelDisplay) ? (
                <>
                  {regDisplay && <strong>{regDisplay}</strong>}
                  {modelDisplay && (
                    <div style={{ fontSize: '0.85em', color: '#7f8c8d' }}>{modelDisplay}</div>
                  )}
                </>
              ) : '-'}
            </td>
            <td style={{ textAlign: 'center', whiteSpace: 'nowrap', paddingRight: '12px' }}>
              <div>{item.fstat_cabin_type ? cabinTypes[item.fstat_cabin_type] : '-'}</div>
              <div style={{ fontSize: '0.85em', color: '#7f8c8d', marginTop: '2px' }}>
                {item.fstat_seat_row && item.fstat_seat_col ? (
                  <>
                    <strong>{item.fstat_seat_row}{item.fstat_seat_col}</strong>
                    {item.fstat_seat_type && ` (${seatTypes[item.fstat_seat_type]})`}
                  </>
                ) : '-'}
              </div>
            </td>
          </>
        );
        }}
      />
    </div>
  );
}

export default FlightsStatList;
